/**
 * VistoeraTASK-00556 — Canvas READ_ONLY access mode (single shared Canvas).
 *
 * The Canvas is NOT forked: `CanvasBoard` receives `readOnly` and layers the
 * guards below on top of the unchanged EDITABLE code path. Layers (any single
 * one is enough to stop a write; they are stacked on purpose so a future node
 * type that grows a new write path still fails closed):
 *
 *  1. Persistence — `doSave` / `scheduleFromState` become no-ops (CanvasBoard).
 *  2. React Flow — no drag / connect / reconnect / delete key; `remove`,
 *     `position`, `add`, `replace` and resize changes are dropped
 *     (`filterReadOnlyNodeChanges` / `filterReadOnlyEdgeChanges`).
 *  3. Node data — every function prop handed to a node is replaced by a
 *     denied no-op, except the view-only allowlist (`lockCanvasNodeData`).
 *  4. DOM — text inputs / textareas become `readOnly` and write-type native
 *     events are blocked (`installCanvasReadOnlyDomGuard`).
 *  5. Network (canvasReadOnlyFence.ts) — a request interceptor on `saasApi` rejects every non-GET
 *     request issued while a read-only Canvas is mounted, except a tiny
 *     allowlist (`installCanvasReadOnlyNetworkFence`). This also covers
 *     workspace-level generation tasks that cannot be project-guarded by the
 *     backend.
 *  6. Backend — `content_project_access_service` rejects project-bound writes
 *     independently of anything above.
 */

import type { EdgeChange, NodeChange } from '@xyflow/react'

export type CanvasAccessMode = 'EDITABLE' | 'READ_ONLY'

/** Node-data callbacks that only change local VIEW state (selection / which
 * card is expanded / pan to a node) or resolve read-only data. */
const VIEW_ONLY_CALLBACKS = new Set<string>([
  'onToggleDetail',
  'onSelectNode',
  'onExpandFunctionCardDetail',
  'onToggleCollapsed',
  'onFocusVideoKeyframeSourceNode',
])

/** Read-only resolver callbacks (`onResolve…`) return data, never write. */
const VIEW_ONLY_CALLBACK_PREFIXES = ['onResolve', 'onPreview', 'onOpenPreview', 'onOpenMedia']

export function isViewOnlyNodeCallback(key: string): boolean {
  if (VIEW_ONLY_CALLBACKS.has(key)) return true
  return VIEW_ONLY_CALLBACK_PREFIXES.some((p) => key.startsWith(p))
}

export const CANVAS_READ_ONLY_DENIED_CODE = 'CANVAS_READ_ONLY'

let deniedNotifier: (() => void) | null = null
let lastDeniedAt = 0

/** The Canvas registers its (throttled) "read-only project" toast here. */
export function setCanvasReadOnlyDeniedNotifier(fn: (() => void) | null): void {
  deniedNotifier = fn
}

let lastUserGestureAt = 0
const USER_GESTURE_WINDOW_MS = 1000

/** Denied no-ops can also be hit by node effects (auto-normalisation on
 * mount). Only tell the user "read-only" when a real gesture just happened. */
export function notifyCanvasReadOnlyDenied(): void {
  const now = Date.now()
  if (now - lastUserGestureAt > USER_GESTURE_WINDOW_MS) return
  if (now - lastDeniedAt < 1500) return
  lastDeniedAt = now
  deniedNotifier?.()
}

/** Stable denied no-op: resolves to `undefined` because callers frequently
 * `await` / `.then` the result. */
export const readOnlyDeniedNoop = (..._args: unknown[]): Promise<undefined> => {
  notifyCanvasReadOnlyDenied()
  return Promise.resolve(undefined)
}

/** Replace every non-allowlisted function in a node's `data` with the denied
 * no-op. */
export function lockCanvasNodeData<T extends Record<string, unknown>>(data: T): T {
  const out: Record<string, unknown> = { ...data }
  for (const key of Object.keys(out)) {
    if (typeof out[key] !== 'function') continue
    if (isViewOnlyNodeCallback(key)) continue
    out[key] = readOnlyDeniedNoop
  }
  out.canvasReadOnly = true
  return out as T
}

/** Node changes a read-only viewer may still apply: selection and automatic
 * size measurement. Everything that mutates the graph is dropped. */
export function filterReadOnlyNodeChanges(changes: NodeChange[]): NodeChange[] {
  return changes.filter((c) => {
    if (c.type === 'select') return true
    // Automatic ResizeObserver measurement never sets `resizing`; a real user
    // resize (Scene NodeResizer) always does.
    if (c.type === 'dimensions') return typeof c.resizing !== 'boolean'
    return false
  })
}

export function filterReadOnlyEdgeChanges(changes: EdgeChange[]): EdgeChange[] {
  return changes.filter((c) => c.type === 'select')
}

// ---------------------------------------------------------------------------
// DOM guard
// ---------------------------------------------------------------------------

const WRITE_EVENT_TYPES = ['beforeinput', 'paste', 'cut', 'drop'] as const

function lockEditableElements(root: ParentNode): void {
  root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('textarea, input').forEach((el) => {
    if (el instanceof HTMLInputElement) {
      const t = (el.type || 'text').toLowerCase()
      if (['checkbox', 'radio', 'range', 'file'].includes(t)) {
        if (!el.disabled) el.disabled = true
        return
      }
    }
    if (!el.readOnly) el.readOnly = true
  })
  root.querySelectorAll<HTMLElement>('[contenteditable="true"]').forEach((el) => {
    el.setAttribute('contenteditable', 'false')
  })
}

/** Marks the Canvas root and keeps every (current and future) text input read
 * only. Returns an uninstall function. */
export function installCanvasReadOnlyDomGuard(root: HTMLElement): () => void {
  root.setAttribute('data-canvas-readonly', 'true')
  lockEditableElements(root)
  // Coalesce bursts of DOM mutations (React Flow re-renders) into one pass per
  // frame so the guard never becomes a per-mutation cost.
  let raf = 0
  const schedule = () => {
    if (raf) return
    raf = window.requestAnimationFrame(() => {
      raf = 0
      lockEditableElements(root)
      document
        .querySelectorAll<HTMLElement>('.ant-modal-wrap, .ant-drawer, .ant-popover')
        .forEach((el) => lockEditableElements(el))
    })
  }
  const observer = new MutationObserver(schedule)
  observer.observe(root, { childList: true, subtree: true })
  // Portaled overlays (ant modals / popovers) are appended directly to body.
  const bodyObserver = new MutationObserver(schedule)
  bodyObserver.observe(document.body, { childList: true })

  const block = (e: Event) => {
    const target = e.target as HTMLElement | null
    if (!target) return
    if (e.type === 'drop') {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (target.closest('input, textarea, [contenteditable]')) {
      e.preventDefault()
      e.stopPropagation()
      notifyCanvasReadOnlyDenied()
    }
  }
  for (const type of WRITE_EVENT_TYPES) root.addEventListener(type, block, true)
  const markGesture = () => {
    lastUserGestureAt = Date.now()
  }
  window.addEventListener('pointerdown', markGesture, true)
  window.addEventListener('keydown', markGesture, true)

  return () => {
    window.removeEventListener('pointerdown', markGesture, true)
    window.removeEventListener('keydown', markGesture, true)
    observer.disconnect()
    bodyObserver.disconnect()
    if (raf) window.cancelAnimationFrame(raf)
    for (const type of WRITE_EVENT_TYPES) root.removeEventListener(type, block, true)
    root.removeAttribute('data-canvas-readonly')
  }
}
